import React, { useMemo, useState } from "react";

import { queryCache } from "react-query";
import { Redirect } from "react-router-dom";

import ROUTES from "manager/config/routes";
import { COMPANY_QUERY_KEYS } from "manager/hooks/api/companyQueries";
import {
  PORTFOLIO_QUERY_KEYS,
  useGetVeroGoSettingsData,
  useToggleVeroGoAll,
} from "manager/hooks/api/portfolioQueries";
import SettingsPage from "manager/templates/SettingsPage";
import Container from "shared/components/Container";
import LoaderContainer, {
  BigLoadingSpinner,
} from "shared/components/LoaderContainer";
import { ViewOnlyBanner } from "shared/components/ViewOnlyBanner";
import { usePermissions } from "shared/hooks";
import { useConfigLock } from "shared/hooks/useConfigLock";
import useModal from "shared/hooks/useModal";
import {
  getCachedActiveCompanyId,
  getCachedActiveCompanySlug,
} from "shared/utils/auth";

import {
  CustomPropertyCard,
  GreenSwitch,
  PortfolioEmptyState,
  PortfolioSection,
  VeroGoSettingsModal,
} from "./components";

interface VeroGoSettingsPageProps {
  backLink: { to: string; children: string };
}

const FLASH_ANIMATION_DURATION_MS = 1000;
const FLASH_CLEAR_DELAY_MS = FLASH_ANIMATION_DURATION_MS + 100;

const VeroGoSettingsPage = ({ backLink }: VeroGoSettingsPageProps) => {
  const { openModalDialog } = useModal();
  const { isLocked } = useConfigLock();
  const { isVeroGoEnabled, permissionsLoaded } = usePermissions();
  const [filter, setFilter] = useState("enabled");
  const [expandedPortfolioId, setExpandedPortfolioId] = useState<number | null>(
    null
  );
  const [pageLoading, setPageLoading] = useState(false);
  const [flashTarget, setFlashTarget] = useState<{
    type: string;
    id: number;
  } | null>(null);
  const handleStartMutation = (itemType?: string, itemId?: number) => {
    setPageLoading(true);
    if (itemType && itemId != null) {
      setFlashTarget({ type: itemType, id: itemId });
    }
  };

  const { veroGoSettingsData, isVeroGoSettingsLoading } =
    useGetVeroGoSettingsData({ enabled: isVeroGoEnabled });
  const portfolios = veroGoSettingsData?.portfolios || [];
  const properties = veroGoSettingsData?.properties || [];

  const {
    propertiesByPortfolio,
    customPropertyIds,
    customProperties,
    portfoliosById,
  } = useMemo(() => {
    const byId: Record<number, any> = {};
    portfolios.forEach((p: any) => {
      byId[p.id] = p;
    });

    const byPortfolio: Record<number, any[]> = {};
    const customIds = new Set<number>();
    const customs: any[] = [];

    properties.forEach((property: any) => {
      const pid = property.portfolio;
      if (!byPortfolio[pid]) byPortfolio[pid] = [];
      byPortfolio[pid].push(property);

      const hasCustomVeroGoSetting = property.isVeroGoFromPortfolio === false;
      const hasCustomSettings =
        !property.isDealSetupFromPortfolio ||
        !property.isPrescreeningDealSetupFromPortfolio ||
        hasCustomVeroGoSetting;
      if (hasCustomSettings) {
        customIds.add(property.id);
        customs.push(property);
      }
    });

    return {
      propertiesByPortfolio: byPortfolio,
      customPropertyIds: customIds,
      customProperties: customs,
      portfoliosById: byId,
    };
  }, [properties, portfolios]);

  const handleAfterToggle = async (newValue: boolean) => {
    await queryCache.refetchQueries(
      PORTFOLIO_QUERY_KEYS.getVeroGoSettingsData()
    );
    if (filter === "enabled" && !newValue) setFilter("disabled");
    else if (filter === "disabled" && newValue) setFilter("enabled");
    setPageLoading(false);
  };

  const handleAfterReset = async (portfolioId: number) => {
    await queryCache.refetchQueries(
      PORTFOLIO_QUERY_KEYS.getVeroGoSettingsData()
    );
    const portfolio = portfoliosById[portfolioId];
    if (portfolio) {
      if (filter === "enabled" && !portfolio.veroGoActive)
        setFilter("disabled");
      else if (filter === "disabled" && portfolio.veroGoActive)
        setFilter("enabled");
    }
    setExpandedPortfolioId(portfolioId);
    setTimeout(() => setExpandedPortfolioId(null), 500);
    setPageLoading(false);
  };

  React.useEffect(() => {
    if (flashTarget && !pageLoading) {
      const timer = setTimeout(
        () => setFlashTarget(null),
        FLASH_CLEAR_DELAY_MS
      );
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [flashTarget, pageLoading]);

  React.useEffect(() => {
    if (!flashTarget || pageLoading) return undefined;

    const frame = window.requestAnimationFrame(() => {
      const targetKey = `${flashTarget.type}-${flashTarget.id}`;
      const target = document.querySelector(
        `[data-vero-go-item="${targetKey}"]`
      ) as HTMLElement | null;

      if (!target) return;

      const rect = target.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      const isVisible = rect.top >= 80 && rect.bottom <= viewportHeight - 24;

      if (!isVisible) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [flashTarget, pageLoading]);

  const getInheritingProperties = (portfolioId: number) => {
    const props = propertiesByPortfolio[portfolioId] || [];
    return props.filter((p: any) => !customPropertyIds.has(p.id));
  };

  const filteredPortfolios = useMemo(() => {
    if (filter === "enabled")
      return portfolios.filter((p: any) => p.veroGoActive);
    if (filter === "disabled")
      return portfolios.filter((p: any) => !p.veroGoActive);
    return portfolios;
  }, [portfolios, filter]);

  const filteredCustomProperties = useMemo(() => {
    if (filter === "enabled")
      return customProperties.filter((p: any) => p.veroGoActive);
    if (filter === "disabled")
      return customProperties.filter((p: any) => !p.veroGoActive);
    return customProperties;
  }, [customProperties, filter]);

  const tabCounts = useMemo(() => {
    const enabledPortfolios = portfolios.filter(
      (p: any) => p.veroGoActive
    ).length;
    const disabledPortfolios = portfolios.length - enabledPortfolios;
    const enabledCustomProperties = customProperties.filter(
      (p: any) => p.veroGoActive
    ).length;
    const disabledCustomProperties =
      customProperties.length - enabledCustomProperties;

    return {
      enabled: {
        total: enabledPortfolios + enabledCustomProperties,
        portfolios: enabledPortfolios,
        customProperties: enabledCustomProperties,
      },
      disabled: {
        total: disabledPortfolios + disabledCustomProperties,
        portfolios: disabledPortfolios,
        customProperties: disabledCustomProperties,
      },
      all: {
        total: portfolios.length + customProperties.length,
        portfolios: portfolios.length,
        customProperties: customProperties.length,
      },
    };
  }, [customProperties, portfolios]);

  const { toggleVeroGoAll, isToggleVeroGoAllLoading } = useToggleVeroGoAll();

  const allActive = Boolean(veroGoSettingsData?.allActive);

  const handleToggleAll = async (checked: boolean) => {
    if (checked && !allActive) {
      // @ts-ignore afterClose exists at runtime (ModalManager.js)
      const { afterClose } = openModalDialog(VeroGoSettingsModal, {
        title: "Enable Vero Go for All",
        bodyText:
          "Are you sure you want to enable Vero Go for every portfolio and property?",
        submitButtonLabel: "Confirm",
        cancelLinkLabel: "Cancel",
        confirmValue: "confirm",
      });
      const result = await afterClose;
      if (result !== "confirm") return;
    }
    if (!checked && allActive) {
      // @ts-ignore afterClose exists at runtime (ModalManager.js)
      const { afterClose } = openModalDialog(VeroGoSettingsModal, {
        title: "Disable Vero Go for All",
        bodyText:
          "Are you sure you want to disable Vero Go for every portfolio and property?",
        submitButtonLabel: "Confirm",
        cancelLinkLabel: "Cancel",
        confirmValue: "confirm",
      });
      const result = await afterClose;
      if (result !== "confirm") return;
    }

    handleStartMutation();
    toggleVeroGoAll(checked, {
      onSettled: async () => {
        await queryCache.refetchQueries(
          PORTFOLIO_QUERY_KEYS.getVeroGoSettingsData()
        );
        setFilter(checked ? "enabled" : "disabled");
        setPageLoading(false);
      },
    });
  };

  const isLoading = isVeroGoSettingsLoading;

  React.useEffect(() => {
    const cachedCompany =
      getCachedActiveCompanyId() || getCachedActiveCompanySlug();
    if (!cachedCompany) return;

    queryCache.refetchQueries(COMPANY_QUERY_KEYS.getCompany(cachedCompany));
  }, []);

  if (permissionsLoaded && !isVeroGoEnabled) {
    return <Redirect to={ROUTES.settings} />;
  }

  return (
    <SettingsPage
      title="Vero Go"
      subtitle="Enable document-only income verification per portfolio and property"
      backLink={backLink}
    >
      {isLoading ? (
        <LoaderContainer
          Loader={<BigLoadingSpinner data-testid="loading" />}
          fetching
        />
      ) : (
        <Container noPaddingTop style={{ position: "relative" }}>
          {/* eslint-disable-next-line react/no-unknown-property */}
          <style>{`
            @keyframes veroGoFocusPulse {
              0% {
                background-color: rgba(124, 58, 237, 0.1);
              }
              65% {
                background-color: rgba(124, 58, 237, 0.03);
              }
              100% {
                background-color: #ffffff;
              }
            }
            @keyframes veroGoHorizontalShine {
              0% {
                left: -38%;
                opacity: 0;
              }
              20% {
                opacity: 1;
              }
              100% {
                left: 110%;
                opacity: 0;
              }
            }
            .vero-go-flash {
              position: relative;
              overflow: hidden;
              background-color: #ffffff;
              transition: background-color 320ms ease;
              will-change: background-color;
              animation: veroGoFocusPulse ${FLASH_ANIMATION_DURATION_MS}ms ease-out forwards;
            }
            .vero-go-flash::after {
              content: "";
              position: absolute;
              top: 0;
              left: -38%;
              width: 34%;
              height: 100%;
              pointer-events: none;
              background: linear-gradient(
                90deg,
                transparent,
                rgba(255, 255, 255, 0.32),
                transparent
              );
              animation: veroGoHorizontalShine ${FLASH_ANIMATION_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
            }
          `}</style>
          {pageLoading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.6)",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BigLoadingSpinner />
            </div>
          )}
          {isLocked && <ViewOnlyBanner />}
          <div>
            <div
              style={{
                background: "#fff",
                border: "1px solid #e8e8e8",
                borderRadius: 8,
                padding: "20px",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <strong>
                    {allActive
                      ? "Disable for All Portfolios & Properties"
                      : "Enable for All Portfolios & Properties"}
                  </strong>
                  <p
                    style={{
                      margin: "4px 0 0",
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    This will {allActive ? "disable" : "enable"} Vero Go across
                    every portfolio and property
                  </p>
                </div>
                <GreenSwitch
                  checked={allActive}
                  loading={isToggleVeroGoAllLoading}
                  onChange={handleToggleAll}
                  disabled={isToggleVeroGoAllLoading || isLocked}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  borderBottom: "1px solid #e5e7eb",
                  gap: 20,
                }}
              >
                {[
                  { key: "all", label: "All" },
                  { key: "enabled", label: "Enabled" },
                  { key: "disabled", label: "Disabled" },
                ].map((item) => {
                  const isActive = filter === item.key;
                  const count =
                    tabCounts[item.key as keyof typeof tabCounts].total;

                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setFilter(item.key)}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        padding: "0 0 10px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        color: isActive ? "#111827" : "#8C8C8C",
                        fontSize: 15,
                        fontWeight: 500,
                        lineHeight: 1,
                        borderBottom: isActive
                          ? "3px solid #111827"
                          : "3px solid transparent",
                      }}
                    >
                      <span>{item.label}</span>
                      {count > 0 && (
                        <span
                          style={{
                            minWidth: 22,
                            height: 22,
                            borderRadius: 999,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 6px",
                            fontSize: 12,
                            fontWeight: 600,
                            background: isActive ? "#111827" : "#F3F4F6",
                            border: `1px solid ${
                              isActive ? "#111827" : "#E5E7EB"
                            }`,
                            color: isActive ? "#fff" : "#9CA3AF",
                          }}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 24,
                  marginTop: 14,
                  color: "#8C8C8C",
                  fontSize: 14,
                }}
              >
                {tabCounts[filter as keyof typeof tabCounts].portfolios > 0 && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: "#3b82f6",
                      }}
                    />
                    {tabCounts[filter as keyof typeof tabCounts].portfolios}{" "}
                    portfolios
                  </span>
                )}
                {tabCounts[filter as keyof typeof tabCounts].customProperties >
                  0 && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: "#7c3aed",
                      }}
                    />
                    {
                      tabCounts[filter as keyof typeof tabCounts]
                        .customProperties
                    }{" "}
                    custom properties
                  </span>
                )}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.8px",
                  textTransform: "uppercase",
                  color: "#999",
                }}
              >
                Portfolios
              </span>
            </div>

            {filteredPortfolios.map((portfolio: any) => (
              <PortfolioSection
                key={portfolio.id}
                portfolio={portfolio}
                properties={getInheritingProperties(portfolio.id)}
                isLocked={isLocked}
                forceExpand={expandedPortfolioId === portfolio.id}
                onAfterToggle={handleAfterToggle}
                onStartMutation={handleStartMutation}
                flash={
                  !pageLoading &&
                  flashTarget?.type === "portfolio" &&
                  flashTarget?.id === portfolio.id
                }
                flashPropertyId={
                  !pageLoading && flashTarget?.type === "property"
                    ? flashTarget?.id
                    : undefined
                }
              />
            ))}
            {filteredPortfolios.length === 0 && (
              <PortfolioEmptyState
                filter={filter}
                onSwitchToAll={() => setFilter("all")}
              />
            )}

            {filteredCustomProperties.length > 0 && (
              <>
                <div
                  style={{
                    borderTop: "1px dashed #e8e8e8",
                    margin: "32px 0 0",
                  }}
                />
                <div style={{ marginTop: 24 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: "0.8px",
                          textTransform: "uppercase",
                          color: "#999",
                        }}
                      >
                        Custom Properties
                      </span>
                    </div>
                  </div>
                  {filteredCustomProperties.map((property: any) => {
                    const portfolio = portfoliosById[property.portfolio];
                    return (
                      <CustomPropertyCard
                        key={property.id}
                        property={property}
                        portfolioName={portfolio?.name || "Unknown"}
                        portfolioActive={!!portfolio?.veroGoActive}
                        isLocked={isLocked}
                        onAfterReset={handleAfterReset}
                        onAfterToggle={handleAfterToggle}
                        onStartMutation={handleStartMutation}
                        flash={
                          !pageLoading &&
                          flashTarget?.type === "property" &&
                          flashTarget?.id === property.id
                        }
                      />
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </Container>
      )}
    </SettingsPage>
  );
};

export default VeroGoSettingsPage;
